// @flow

import Dispatcher from 'dispatcher';
import ApiClient from 'utils/ApiClient';
import * as ActionTypes from 'action_types/HomeApplicationActionTypes';
import HomeApplication from 'models/HomeApplication';
import Asset from 'models/homes/Asset';

class HomeApplicationActions {
  getHomeApplications(
    id: string,
    success: any = () => {},
    error: any = () => {}
  ): Promise<any> {
    return ApiClient.get({
      path: `/users/${id}/home_applications.json`,
      skipRedirect: true,
      success: response => {
        Dispatcher.dispatch({
          type: ActionTypes.LIST_HOME_APPLICATIONS,
          home_applications: response.home_applications.map(
            home_application => new HomeApplication(home_application)
          )
        });
        success();
      },
      error: () => {
        error();
      }
    });
  }

  editHomeApplication(id: string, error?: () => void): Promise<any> {
    return ApiClient.get({
      path: `/home_applications/${id}/edit.json`,
      success: response => {
        Dispatcher.dispatch({
          type: ActionTypes.EDIT_HOME_APPLICATION,
          home_application: new HomeApplication(response.home_application)
        });
      },
      error: () => {
        error();
      }
    });
  }

  createHomeApplication(
    params: any,
    success?: (response?: any) => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: '/home_applications.json',
      data: params,
      success: response => {
        if (response.url_redirect) {
          window.location.href = response.url_redirect;
          return;
        }
        Dispatcher.dispatch({
          type: ActionTypes.CREATE_HOME_APPLICATIONS,
          home_application: new HomeApplication(response.home_application)
        });
        if (success) {
          success(new HomeApplication(response.home_application));
        }
      },
      error: response => {
        error && error(response);
      }
    });
  }

  updateHomeApplication(
    home_application_id: string,
    data: any,
    success?: () => void,
    errorFunction?: () => void
  ): Promise<any> {
    return ApiClient.patch({
      path: `/home_applications/${home_application_id}.json`,
      jsonData: data,
      success: response => {
        const home_application = new HomeApplication(response.home_application);
        Dispatcher.dispatch({
          type: ActionTypes.UPDATE_HOME_APPLICATIONS,
          home_application: home_application
        });
        if (success !== undefined) {
          success(home_application);
        }
      },
      error: error => {
        if (
          error.status === 404 ||
          error.status === 400 ||
          error.status === 403 ||
          error.status === 500
        ) {
          Dispatcher.dispatch({
            type: ActionTypes.GET_HOME_APPLICATION_UPDATE_ERROR,
            error: { error: error.responseJSON.message }
          });
          errorFunction && errorFunction();
        }
      }
    });
  }

  updateUserHomeApplication(
    user_id: string,
    home_application_id: string,
    data: any,
    success?: () => void
  ): Promise<any> {
    return ApiClient.patch({
      path: `/users/${user_id}/home_applications/${home_application_id}.json`,
      jsonData: data,
      success: response => {
        if (!data.home_application.ignore_response) {
          Dispatcher.dispatch({
            type: ActionTypes.UPDATE_HOME_APPLICATIONS,
            home_application: new HomeApplication(response.home_application)
          });
        }
        if (success !== undefined) {
          success();
        }
      }
    });
  }

  cancelHomeApplication(
    home_application_id: string,
    user_id: string,
    success?: () => void
  ): Promise<any> {
    return ApiClient.delete({
      path: `/users/${user_id}/home_applications/${home_application_id}`,
      success: () => {
        Dispatcher.dispatch({
          type: ActionTypes.CANCEL_HOME_APPLICATION
        });

        if (success !== undefined) {
          success();
        }
      }
    });
  }

  listHomeApplicationAssets(
    home_application_id: string,
    asset_type: string
  ): Promise<any> {
    return ApiClient.get({
      path: `/home_applications/${home_application_id}/assets/${asset_type}.json`,
      success: response => {
        Dispatcher.dispatch({
          type: ActionTypes.LIST_HOME_APPLICATION_ASSETS,
          assets: {
            [asset_type]: response.assets.map(
              asset =>
                new Asset(asset.id, asset.file_name, asset.url, asset_type)
            )
          }
        });
      }
    });
  }

  validateHomeApplicationPromoCode(
    code: string,
    data: any,
    success?: () => void,
    errorFunction?: () => void
  ): Promise<any> {
    return ApiClient.get({
      path: `/promotion_codes/${code}`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          type: ActionTypes.GET_HOME_APPLICATION_VALIDATE_PROMO_CODE,
          response: response
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error => {
        if (
          error.status === 404 ||
          error.status === 400 ||
          error.status === 403
        ) {
          Dispatcher.dispatch({
            type: ActionTypes.GET_HOME_APPLICATION_VALIDATE_PROMO_CODE_ERROR,
            error: { error: error.responseJSON.message }
          });
          errorFunction();
        }
      }
    });
  }

  uploadAsset(
    home_application_id: string,
    asset_type: string,
    file: File,
    assetTmpId: string,
    success?: () => void
  ): Promise<any> {
    let formData = new FormData();
    formData.append('file', file);
    return ApiClient.uploadFile(
      `/api/internal/home_applications/${home_application_id}/assets/${asset_type}.json`,
      formData,
      str_response => {
        const response = JSON.parse(str_response);
        Dispatcher.dispatch({
          type: ActionTypes.UPLOAD_HOME_APPLICATION_ASSET,
          asset: new Asset(
            response.asset.id,
            response.asset.image_file_name || response.asset.file_name,
            response.asset.image_url || response.asset.url,
            asset_type
          ),
          temp_asset_id: assetTmpId
        });
        if (success) success(str_response);
      },
      'POST'
    );
  }

  addTmpAsset(asset: Asset) {
    Dispatcher.dispatch({
      type: ActionTypes.ADD_TEMP_HOME_APPLICATION_ASSET,
      asset: asset
    });
  }

  deleteAsset(
    home_application_id: string,
    asset_id: string | number,
    asset_type: string,
    success?: () => void
  ): Promise<any> {
    return ApiClient.delete({
      path: `/home_applications/${home_application_id}/assets/${asset_id}.json`,
      data: { asset_type: asset_type },
      skipRedirect: true,
      success: response => {
        Dispatcher.dispatch({
          type: ActionTypes.DELETE_HOME_APPLICATION_ASSET,
          asset_id: asset_id,
          asset_type: asset_type
        });
        if (success) success(asset_id);
      }
    });
  }
}

export default new HomeApplicationActions();
